import { useState, useEffect, useCallback, useRef } from 'react'
import { useDispatch } from 'react-redux'
import { useDiscussionRepository } from '../../../../../../services/adaptors/discussionRepositoryAdapter'
import { TargetThread } from '../../../../../../domain/entity/DiscussionEntity'
import { intl } from '../../../../../../i18n'
import {
  addGlobalMessage,
  MessageLevel,
} from '../../../../../../store/messages'

type UseTargetDiscussionsProps = {
  selectedTargetUuid: string | null
  currentVersionUuid?: string
  templatePageUuid?: string
  enabled?: boolean
}

export const useTargetDiscussions = ({
  selectedTargetUuid,
  currentVersionUuid,
  templatePageUuid,
  enabled = true,
}: UseTargetDiscussionsProps) => {
  const [threads, setThreads] = useState<TargetThread[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const invocationRef = useRef(0)
  const { getThreadsByTarget } = useDiscussionRepository()
  const dispatch = useDispatch()

  const fetch = useCallback(async () => {
    if (!selectedTargetUuid || !currentVersionUuid || !templatePageUuid) {
      setThreads([])
      return
    }
    const myInvocation = ++invocationRef.current
    try {
      setIsLoading(true)
      const result = await getThreadsByTarget(
        selectedTargetUuid,
        currentVersionUuid,
        templatePageUuid
      )
      if (invocationRef.current === myInvocation) {
        setThreads(result)
      }
    } catch {
      if (invocationRef.current === myInvocation) {
        dispatch(
          addGlobalMessage({
            type: MessageLevel.ERROR,
            title: intl.formatMessage({ id: 'discussion.error.fetch' }),
          })
        )
        setThreads([])
      }
    } finally {
      if (invocationRef.current === myInvocation) {
        setIsLoading(false)
      }
    }
  }, [
    selectedTargetUuid,
    currentVersionUuid,
    templatePageUuid,
    getThreadsByTarget,
    dispatch,
  ])

  useEffect(() => {
    if (!enabled) return
    fetch()
  }, [enabled, fetch])

  return { threads, isLoading, refresh: fetch }
}
